import './address-form.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCallback,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import TextField from '../../form-fields/text-field';
import CheckboxField from '../../form-fields/checkbox-field';
import SelectField from '../../form-fields/select-field';
import PhoneField from '../../form-fields/phone-field';
import { COUNTRIES, LOCATION_ADDRESS_MAP, STATES } from '../../../../constants/address';
import AddressSearchField from '../../form-fields/address-search-field';
import { matchTwoAddresses } from '../../../../utilities/utils';
import PrimaryButton from '../../buttons/primary-button/primary-button';
import {
  YUP_CITY_SCHEMA,
  YUP_FIRST_NAME_SCHEMA,
  YUP_LAST_NAME_SCHEMA,
  YUP_PHONE_SCHEMA,
  YUP_POSTAL_CODE_SCHEMA,
  YUP_STREET_SCHEMA,
} from '../../../../utilities/form';

const schema = yup
  .object({
    additionalAddressInfo: yup.string(),
    isDefaultShippingAddress: yup.boolean(),
    firstName: YUP_FIRST_NAME_SCHEMA,
    lastName: YUP_LAST_NAME_SCHEMA,
    phone: YUP_PHONE_SCHEMA,
    streetName: YUP_STREET_SCHEMA,
    apartment: yup.string(),
    city: YUP_CITY_SCHEMA,
    postalCode: YUP_POSTAL_CODE_SCHEMA,
    state: yup.string(),
    country: yup.string(),
  })
  .required();

const AddressForm = ({
  defaultValues, onCancel, onSubmit, submitting,
}) => {
  const addresses = useSelector((state) => state.accounts.user.addresses);

  const { additionalAddressInfo, ...restDefaultValues } = defaultValues ?? {};

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      nickname: additionalAddressInfo ?? '',
      isDefaultShippingAddress: false,
      firstName: '',
      lastName: '',
      phone: '',
      streetName: '',
      apartment: '',
      city: '',
      postalCode: '',
      state: STATES[0].value,
      country: COUNTRIES[0].value,
      ...(restDefaultValues ?? {}),
    },
  });

  const handleAddressAutocomplete = useCallback((addressComponents) => {
    let fullAddress = '';
    const newVals = {};
    addressComponents.forEach(({ short_name: shortName, types }) => {
      const type = types[0];
      switch (type) {
        case 'street_number':
          fullAddress = `${shortName} ${fullAddress}`;
          break;
        case 'route':
          fullAddress = `${fullAddress}${shortName}`;
          break;
        default:
          break;
      }

      const mappedField = LOCATION_ADDRESS_MAP[type];
      if (mappedField) {
        newVals[mappedField] = shortName;
      }
    });

    if (fullAddress) {
      methods.setValue('streetName', fullAddress, { shouldValidate: true });
    }
    if (newVals.postalCode) {
      methods.setValue('postalCode', newVals.postalCode, { shouldValidate: true });
    }
    if (newVals.city) {
      methods.setValue('city', newVals.city, { shouldValidate: true });
    }
    if (newVals.state) {
      methods.setValue('state', newVals.state, { shouldValidate: true });
    }
    if (newVals.country) {
      methods.setValue('country', newVals.country, { shouldValidate: true });
    }
  }, [methods]);

  const handleSubmit = useCallback((formValues) => {
    const isUsedAddress = !!addresses.find(
      (addr) =>
        matchTwoAddresses(
          { street: addr.streetName, postalCode: addr.postalCode },
          {
            street: formValues.streetName,
            postalCode: formValues.postalCode,
          },
        ) && (!defaultValues?.id || addr.id !== defaultValues?.id),
    );

    if (isUsedAddress) {
      methods.setError(
        'streetName',
        { message: 'You already have this address saved.' },
      );
    } else {
      onSubmit({
        ...formValues,
        additionalAddressInfo: formValues.nickname,
      });
    }
  }, [defaultValues?.id, addresses, methods, onSubmit]);

  return (
    <div className="address-form">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <div className="address-form__content">
            <div className="address-form__section">
              <div className="address-form__section__title">
                <h4>General</h4>
              </div>

              <div className="address-form__section__form">
                <TextField
                  autocomplete="off"
                  name="nickname"
                  placeholder="Example: Home"
                  label="Address nickname"
                />
                <CheckboxField
                  label="Set as primary address"
                  name="isDefaultShippingAddress"
                />
              </div>
            </div>

            <div className="address-form__section">
              <div className="address-form__section__title">
                <h4>Address</h4>
              </div>

              <div className="address-form__section__form">
                <AddressSearchField
                  name="streetName"
                  label="Address line 1 *"
                  placeholder=""
                  onAutocomplete={handleAddressAutocomplete}
                />
                <TextField
                  name="apartment"
                  label="Address line 2"
                />

                <div className="address-form__section__form__row">
                  <TextField
                    name="city"
                    label="City *"
                  />
                  <SelectField
                    name="state"
                    label="State *"
                    options={STATES}
                    placeholder="Select State"
                  />
                </div>

                <div className="address-form__section__form__row">
                  <TextField
                    name="postalCode"
                    label="Zip code *"
                  />
                  <SelectField
                    name="country"
                    label="Country *"
                    options={COUNTRIES}
                    placeholder="Select Country"
                  />
                </div>
              </div>
            </div>

            <div className="address-form__section">
              <div className="address-form__section__title">
                <h4>Deliver to</h4>
              </div>

              <div className="address-form__section__form">
                <TextField
                  name="firstName"
                  label="First name *"
                />
                <TextField
                  name="lastName"
                  label="Last name *"
                />
                <PhoneField name="phone" label="Phone Number" />
              </div>
            </div>

            <div className="address-form__actions">
              <button
                type="button"
                className="button button--default"
                onClick={onCancel}
              >
                Cancel
              </button>

              <PrimaryButton title="Save" isLoading={submitting} />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddressForm;
